import { CONFIG_SUPPORTED_DEVICES_KB_URL_KEY } from '@/constants/device';
import { apiCallback } from '@vantiva-sa/homesight_humaui/Services/Common';
import { getCurrentUser } from '@vantiva-sa/homesight_humaui/Store/authStore';
import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';
import { getUserConfig, getUserInfo } from '@vantiva-sa/homesight_humaui/Store/dataStore';
import { AppStorageKeys } from '@vantiva-sa/homesight_humaui/Utilities/Constants';
import { getDayLabel } from '@vantiva-sa/homesight_humaui/Utilities/DateTime';
import SessionStorage from '@vantiva-sa/homesight_humaui/Utilities/SessionStorage';
import { get } from 'lodash';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  celsiusToFahrenheit,
  fahrenheitToCelsius,
  getDayLabelOrDate,
  kilogramsToPounds,
  poundsToKilograms,
} from '../../utilities/helpers/commonHelper';
import { useEventStream } from '../Hooks/useEventStream';
import './Wellness.less';
import WellnessHeader from './WellnessHeader';
import WellnessHistoryModal from './WellnessHistoryModal';
import WellnessList from './WellnessList';
import {
  formatValue,
  getStatisticUnitTitle,
  getUserDefinedUnitVal,
  processEventFromStream,
  wellnessEventTypes,
  wellnessListModel,
} from './WellnessUtils';

const LocalTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

const DefaultBiometricsHistoryNumberOfDays = 30;
const ENV_VARS = SessionStorage.get(AppStorageKeys.envVars);
const BIOMETRICS_HISTORY_NUMBER_OF_DAYS = ENV_VARS.BIOMETRICS_HISTORY_NUMBER_OF_DAYS;

const HistoryDays = BIOMETRICS_HISTORY_NUMBER_OF_DAYS ?? DefaultBiometricsHistoryNumberOfDays;

const Wellness = ({
  hubRecord,
  timezone,
  // deviceStatus: _deviceStatus, // Prefixed with _ to indicate intentionally unused
  // providerid: _providerid, // Prefixed with _ to indicate intentionally unused
  loginUserTimeformat,
  loginUserMeasurementUnit,
  // openSubMenu,
}) => {
  const useruuid = hubRecord?.useruuid || '';
  const userTimezone = timezone ? timezone?.split(' ')[0] : LocalTimezone;
  const userInfo = useSelector(getUserInfo(useruuid));
  const trans = useSelector(translator);
  const userEventsApi = useSelector((state) => state.apis.userEventsApi);
  // const deviceModelApi = useSelector((state) => state.apis.deviceModelApi);

  // Get logged-in user's data for locale/time format (always use logged-in user's preferences)
  const userData = useSelector((user) => user.data.userInfo);
  const currentUser = useSelector(getCurrentUser);
  const currentUseruuid = useMemo(() => currentUser?.useruuid, [currentUser]);
  // Always use the logged-in user's preferences for time/date formatting
  const loginUserDataInfo = userData[currentUseruuid];
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  // const [showSupportDeviceModal, setShowSupportDeviceModal] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [wellnessListData, setWellnessListData] = useState([]);
  const [modalLoading, setModalLoading] = useState(false);
  // const [supportDeviceModalLoading, setSupportDeviceModalLoading] = useState(false);
  const [modalData, setModalData] = useState([]);
  // const [supportDeviceData, setSupportDeviceData] = useState([]);
  const [modalTitle, setModalTitle] = useState('');
  // const [supportDeviceModalTitle, setSupportDeviceModalTitle] = useState('');
  const [currentDevice, setCurrentDevice] = useState({});
  // const [wellnessListModel, setWellnessListModel] = useState(null);
  // const [startDatetime, setStartDatetime] = useState('');
  // const [endDatetime, setEndDatetime] = useState('');
  // const [allDeviceModels, setAllDeviceModels] = useState([]);
  // const [activeSupportDevicesTab, setActiveSupportDevicesTab] = useState(
  //   Object.keys(allDeviceModels || {}).length > 0 ? Object.keys(allDeviceModels || {})[0] : ''
  // );
  const [selectedRange, setSelectedRange] = useState('');
  const [insightStartDateTime, setInsightStartDateTime] = useState('');
  const [insightEndDateTime, setInsightEndDateTime] = useState('');
  const [insightData, setInsightData] = useState([]);
  const [insightLoading, setInsightLoading] = useState(false);
  const [currentEventType, setCurrentEventType] = useState('');

  const hubConfig = useSelector(getUserConfig(useruuid));
  const supportedDevicesKbUrl = useMemo(
    () => get(hubConfig, CONFIG_SUPPORTED_DEVICES_KB_URL_KEY, ''),
    [hubConfig]
  );

  const { latestEvent } = useEventStream(userInfo, [...Object.values(wellnessEventTypes)]);

  useEffect(() => {
    processEventFromStream({
      latestEvent,
      userInfo,
      timezone: userTimezone,
      setWellnessListData,
    });
  }, [latestEvent, userInfo, userTimezone]);

  // useEffect(() => {
  //   if (!wellnessListModel) {
  //     try {
  //       const modelData = JSON.parse(JSON.stringify(WellnessListDataModel)); // Deep copy
  //       wellnessListModelData = modelData;
  //       setWellnessListModel(modelData);
  //     } catch (error) {
  //       console.error('Error initializing wellnessListModel:', error);
  //     }
  //   }
  // }, []);

  useEffect(() => {
    if (!currentEventType || !selectedRange) return;
    const computeTimeRange = (subtractAmount, subtractUnit) => {
      const currentMoment = moment().tz(userTimezone);
      const endMoment = currentMoment.clone().add(1, 'hours').startOf('hour');
      const startMoment = endMoment.clone().subtract(subtractAmount, subtractUnit);
      return {
        start: startMoment.clone().utc().format('YYYY-MM-DDTHH:mm:ss[Z]'),
        end: endMoment.clone().utc().format('YYYY-MM-DDTHH:mm:ss[Z]'),
      };
    };
    setInsightLoading(true);
    let startDateTimeUTC, endDateTimeUTC;
    if (selectedRange === 'today') {
      ({ start: startDateTimeUTC, end: endDateTimeUTC } = computeTimeRange(24, 'hours'));
    } else if (selectedRange === 'lastweek') {
      ({ start: startDateTimeUTC, end: endDateTimeUTC } = computeTimeRange(7, 'days'));
    } else if (selectedRange === 'lastmonth') {
      ({ start: startDateTimeUTC, end: endDateTimeUTC } = computeTimeRange(30, 'days'));
    } else if (selectedRange === 'all') {
      ({ start: startDateTimeUTC, end: endDateTimeUTC } = computeTimeRange(90, 'days'));
    }

    setInsightStartDateTime(startDateTimeUTC);
    setInsightEndDateTime(endDateTimeUTC);

    // API callback definitions.
    const errorCallback = (error) => {
      setInsightLoading(false);
      // message.error('Error fetching medical history!');
      console.error('Error fetching medical history:', error);
    };
    const successCallback = (data) => {
      const finalData = processUserEvents(data['user_events'], modalTitle);
      setInsightLoading(false);
      setInsightData(finalData);
    };

    try {
      userEventsApi.userEventsByTypesAndTimeRange(
        useruuid,
        startDateTimeUTC,
        endDateTimeUTC,
        {
          eventtypes: [currentEventType],
          details: true,
        },
        apiCallback({
          translator: trans,
          failCallback: errorCallback,
          errorCallback,
          successCallback,
        })
      );
    } catch (error) {
      errorCallback(error);
    }
  }, [selectedRange, currentEventType, userTimezone]);

  // useEffect(() => {
  //   if (!unitTitleData) {
  //     import('./statistic-title.json')
  //       .then((data) => {
  //         unitTitleData = data.default || data;
  //       })
  //       .catch((err) =>
  //         console.error('Error loading statistic-title.json:', err)
  //       );
  //   }
  // }, []);

  // useEffect(() => {
  //   deviceModelApi.getDeviceModelByType(
  //     'all',
  //     apiCallback({
  //       translator: trans,
  //       successCallback: (allData) => {
  //         console.log('Successfully fetched ALL device models:', allData);
  //         const modelsByType = {};
  //         allData?.devicemodels?.forEach((device) => {
  //           const type = device.devicetype;
  //           if (!modelsByType[type]) modelsByType[type] = [];
  //           modelsByType[type].push(device);
  //         });
  //         console.log('Device models organized by type:', modelsByType);
  //         setAllDeviceModels(modelsByType);
  //       },
  //       errorCallback: (error) => {
  //         console.error('Error fetching ALL device models:', error);
  //       },
  //     })
  //   );
  // }, [deviceModelApi, trans]);

  function formatTimestamp(timestamp, timezone) {
    if (timestamp == null || timestamp == undefined) {
      return { day: '-', time: '-', difference: '-', formattedDate: '-' };
    }
    const now = moment().tz(timezone);
    const inputTime = moment.tz(timestamp, timezone);

    // Use logged-in user's locale for date formatting (not account user's)
    const userLocale = loginUserDataInfo?.locale || 'en-US';
    const dayLabel = getDayLabel(timestamp, timezone);

    let time = '';
    let formattedDate = '';
    let day = '';

    // Only show 'Today' or 'Yesterday', otherwise use formatted date
    // Do NOT show day of week names (Monday, Tuesday, etc.)
    if (dayLabel === 'Today' || dayLabel === 'Yesterday') {
      day = dayLabel;
    } else {
      // For all other dates, use locale-aware date formatting
      formattedDate = getDayLabelOrDate(timestamp, timezone, userLocale);
      day = formattedDate; // Use formatted date as the day display
    }

    // Format time based on logged-in user's preference (24-hour or 12-hour format)
    const is24HourFormat = loginUserTimeformat?.is24hoursformat === true;
    if (is24HourFormat) {
      time = inputTime.format('HH:mm'); // Format time as '09:40'
    } else {
      time = inputTime.format('h:mm A'); // Format time as '9:40 AM'
    }

    // Store formattedDate separately for display logic
    if (!dayLabel) {
      formattedDate = getDayLabelOrDate(timestamp, timezone, userLocale);
    }
    const difference = moment.duration(inputTime.diff(now));
    let differenceText;

    if (difference.asMilliseconds() < 0) {
      // Past data
      const pastDifference = moment.duration(now.diff(inputTime));
      differenceText =
        pastDifference.asMinutes() < 1
          ? trans('JUST_NOW_L')
          : pastDifference.asMinutes() < 60
            ? `${Math.round(pastDifference.asMinutes())} ${trans('MINAGO_L')} `
            : pastDifference.asHours() < 24
              ? `${Math.round(pastDifference.asHours())} ${trans('HOURSAGO_L')} `
              : `${Math.round(pastDifference.asDays())} ${trans('DAYSAGO_L')} `;
    } else {
      // Future data
      differenceText =
        difference.asMinutes() < 1
          ? trans('LATER')
          : difference.asMinutes() < 60
            ? `${trans('IN')} ${Math.round(difference.asMinutes())} ${trans('MIN_L')} `
            : difference.asHours() < 24
              ? `${trans('IN')} ${Math.round(difference.asHours())} ${trans('HOURS_L')} `
              : `${trans('IN')} ${Math.round(difference.asDays())} ${trans('DAYS_L')} `;
    }

    return {
      day,
      time,
      difference: differenceText,
      formattedDate, // Include formatted date for non-Today/Yesterday dates
    };
  }

  function processRawData(data) {
    const processedData = [];
    if (!data || Object.keys(data).length === 0) {
      setWellnessListData([]);
      return processedData;
    }

    if (!wellnessListModel || !wellnessListModel['medical_accessories']) {
      console.error('wellnessListModel or medical_accessories is null or undefined.');
      return processedData;
    }
    const wellnessModel = JSON.parse(JSON.stringify(wellnessListModel));
    const allowedKeys = [];

    Object.keys(data).forEach((key) => {
      const entry = data[key][0];
      allowedKeys.push(key);

      if (!entry) {
        //console.warn(`No entry found for key: ${key}`);
        return;
      }

      let dateTimeObj = formatTimestamp(entry.utc_timestamp_inms, userTimezone);
      let day = dateTimeObj['day'];
      let time = dateTimeObj['time'];
      let timeDifference = dateTimeObj['difference'];
      let formattedDate = dateTimeObj['formattedDate'];

      if (!wellnessModel['medical_accessories'][key]) {
        console.warn(`No medical_accessories data for key: ${key}`);
        wellnessModel['medical_accessories'][key] = {};
      }

      const statisticDataArray = Object.keys(
        wellnessModel['medical_accessories'][key]['data'] || []
      );
      const FinalStatisticDataArray = statisticDataArray.map((item) => {
        const newObj = {};
        const getUserDefinedMeasurementValue = entry?.devicetypeid
          ? getUserDefinedUnitVal(
              entry?.data?.[item],
              entry?.devicetypeid,
              loginUserMeasurementUnit?.measurement
            )
          : '-';
        newObj['display_label'] = getStatisticUnitTitle(
          String(item),
          get(loginUserMeasurementUnit, 'measurement', 'imperial')
        );
        newObj['display_value'] = `${formatValue(getUserDefinedMeasurementValue)}${
          +entry?.data?.[item]?.unit != undefined && entry?.data?.[item]?.unit === 'F' ? '°' : ''
        }`;
        return newObj;
      });
      wellnessModel['medical_accessories'][key]['title'] = key;
      wellnessModel['medical_accessories'][key]['day'] = day;
      wellnessModel['medical_accessories'][key]['time'] = time;
      wellnessModel['medical_accessories'][key]['timeDiff'] = timeDifference;
      wellnessModel['medical_accessories'][key]['formattedDate'] = formattedDate;
      wellnessModel['medical_accessories'][key]['utc_timestamp_inms'] = entry['utc_timestamp_inms'];
      wellnessModel['medical_accessories'][key]['data'] = FinalStatisticDataArray;
      wellnessModel['medical_accessories'][key]['deviceManufacture'] = entry.device_manufacturer;
      wellnessModel['medical_accessories'][key]['deviceModel'] = entry.device_model;
      wellnessModel['medical_accessories'][key]['device_uuid'] = entry.deviceuuid;
      wellnessModel['medical_accessories'][key]['eventName'] = entry.eventname;
    });
    const mappedData = JSON.parse(JSON.stringify(wellnessModel));
    const medicalAccessories = mappedData.medical_accessories;
    Object.keys(medicalAccessories).forEach((key) => {
      if (!allowedKeys.includes(key)) {
        delete medicalAccessories[key];
      }
    });
    setWellnessListData(mappedData);
    return mappedData;
  }

  useEffect(() => {
    let payload = {};
    // if (startDatetime && endDatetime) {
    //   payload = {
    //     userMedicalAccessories: {
    //       startdatetime: startDatetime,
    //       enddatetime: endDatetime,
    //     },
    //   };
    // }

    if (wellnessListModel !== null && wellnessListModel !== undefined) {
      setLoading(true);
      const fetchUserMedicalAccessories = () => {
        const errorCallback = (errors) => {
          console.error('Error fetching wellness api information:', errors);
        };
        const successCallback = (data) => {
          const response = data['user_accessories'];
          processRawData(response);
          setLoading(false);
        };

        try {
          if (!useruuid) return;
          userEventsApi.getUserMedicalAccessories(
            useruuid,
            payload,
            apiCallback({
              translator: trans,
              failCallback: errorCallback,
              errorCallback,
              successCallback,
            })
          );
        } catch (error) {
          errorCallback(error);
        }
      };
      fetchUserMedicalAccessories();
      return () => {
        setLoading(false); // Ensure loading is set to false if component unmounts
      };
    }
  }, [
    userEventsApi,
    useruuid,
    trans,
    loginUserMeasurementUnit?.measurement,
    // startDatetime,
    // endDatetime,
  ]);

  const handleModalClose = () => {
    setShowModal(false);
    setSelectedRange('');
    setSelectedCard(null); // Clear selected card when modal closes
  };

  function getUserDefinedMeasurementValue(value, title, itemObj = {}) {
    const userDefinedMeasurement = loginUserMeasurementUnit?.measurement || '';
    if (title === 'thermometer') {
      if (userDefinedMeasurement === 'metric' && itemObj?.temperature_unit?.toLowerCase() === 'f') {
        return fahrenheitToCelsius(value);
      } else if (
        userDefinedMeasurement === 'imperial' &&
        itemObj?.temperature_unit?.toLowerCase() === 'c'
      ) {
        return celsiusToFahrenheit(value);
      } else return value;
    }
    // [HOMESIGHTVTV-9271] Removed glucose meter conversion
    // else if (title === 'glucose_meter') {
    //   if (userDefinedMeasurement === "metric") {
    //     return mgTommol(value);
    //   } else
    //     return value;
    // }
    else if (title === 'weight_scale') {
      if (userDefinedMeasurement === 'metric' && itemObj.weight_unit.toLowerCase() === 'lbs') {
        return poundsToKilograms(value);
      } else if (
        userDefinedMeasurement === 'imperial' &&
        ['kgs', 'kg'].includes(itemObj?.weight_unit?.toLowerCase())
      ) {
        return kilogramsToPounds(value);
      } else return value;
    } else {
      return value;
    }
  }

  function processUserEvents(userEvents, title) {
    if (!userEvents || userEvents.length === 0) {
      return [];
    }

    if (!wellnessListModel) {
      console.error('wellnessListModel is null or undefined.');
      return [];
    }

    // Make sure medical_accessories exists
    if (!wellnessListModel.medical_accessories) {
      console.error('medical_accessories not found in wellnessListModel');
      return [];
    }

    if (!wellnessListModel.medical_accessories[title]) {
      console.error(`No medical_accessories data found for title: ${title}`);
      wellnessListModel.medical_accessories[title] = {
        data: {}, // Empty data object as fallback
      };
    }

    return userEvents
      .sort((a, b) => new Date(b.utc_timestamp_inms) - new Date(a.utc_timestamp_inms))
      .map((event) => {
        const outputObj = {};
        const timeobj = formatTimestamp(event.utc_timestamp_inms, userTimezone);
        outputObj['useruuid'] = event['useruuid'];
        outputObj['deviceuuid'] = event['deviceuuid'];
        outputObj['formatted_date'] = timeobj['day'];
        outputObj['formatted_time'] = timeobj['time'];
        outputObj['formattedDate'] = timeobj['formattedDate'];
        outputObj['modelnumber'] = event['modelnumber'];
        outputObj['timeDiffText'] = timeobj['difference'];
        outputObj['utc_timestamp_inms'] = event['utc_timestamp_inms'];
        outputObj['manufacturer'] = event['manufacturer'];
        outputObj['modelnumber'] = event['modelnumber'];

        let data = {};
        // Safely access the data property, with better fallback handling
        const statisticDataArray = Object.keys(
          wellnessListModel.medical_accessories[title]?.data || {}
        );
        statisticDataArray.forEach((item) => {
          if (item === 'pulse') {
            data['pulse'] = event['pulse_rate'];
          } else if (item === 'oxygen') {
            data['oxygen'] = event['spo2'];
          } else {
            data[item] = getUserDefinedMeasurementValue(event[item], title, event);
          }
        });
        outputObj['data'] = data;
        return outputObj;
      });
  }

  const handelShowModal = (title, deviceBrand, deviceModel, eventType) => {
    if (!wellnessListModel) {
      console.error('wellnessListModel not initialized yet');
      // message.error('System is not ready yet. Please try again in a moment.');
      return;
    }

    const currentDeviceObj = { deviceBrand, deviceModel };
    setCurrentEventType(eventType);
    setCurrentDevice(currentDeviceObj);
    setSelectedRange('lastweek');
    setModalTitle(title); // Set this early to prevent timing issues
    setSelectedCard(`${title}-${deviceBrand}-${deviceModel}`); // Track selected card

    const errorCallback = (error) => {
      // message.error('Error fetching medical history!');
      console.error('Error fetching medical history:', error);
      setModalLoading(false); // Make sure to reset loading state on error
    };

    const successCallback = (data) => {
      if (!data || !data['user_events']) {
        console.error('No user events data returned from API');
        setModalData([]);
        setModalLoading(false);
        return;
      }

      const finalData = processUserEvents(data['user_events'], title);
      setModalData(finalData);
      setModalLoading(false);
    };

    // const endMoment = endDatetime ? moment.utc(endDatetime) : moment.utc();
    const endMoment = moment.utc();
    const endDate = endMoment
      .clone()
      .set({ hour: 23, minute: 59, second: 59 })
      .format('YYYY-MM-DD[T]HH:mm:ss[Z]');

    const startDate = endMoment
      .clone()
      .subtract(HistoryDays, 'days')
      .startOf('day')
      .format('YYYY-MM-DD[T]HH:mm:ss[Z]');

    try {
      setModalLoading(true);
      setShowModal(true);
      userEventsApi.userEventsByTypesAndTimeRange(
        useruuid,
        startDate,
        endDate,
        {
          eventtypes: [eventType],
          details: true,
        },
        apiCallback({
          translator: trans,
          failCallback: errorCallback,
          errorCallback,
          successCallback,
        })
      );
    } catch (error) {
      errorCallback(error);
    }
  };

  const handleSupportedDevicesClick = useCallback(() => {
    if (supportedDevicesKbUrl) {
      window.open(supportedDevicesKbUrl, '_blank', 'noopener,noreferrer');
    }
  }, [supportedDevicesKbUrl]);

  // const handleSupportDeviceModal = (device_type) => {
  //   setShowSupportDeviceModal(true);
  //   setSupportDeviceModalTitle(device_type);
  //   setActiveSupportDevicesTab(device_type);
  //   setSupportDeviceModalLoading(true);

  //   const errorCallback = (error) => {
  //     // message.error('Error fetching support device data!');
  //     console.error('Error fetching support device data:', error);
  //   };
  //   const successCallback = (data) => {
  //     setSupportDeviceData(data['devicemodels']);
  //     setSupportDeviceModalLoading(false);
  //   };

  //   try {
  //     deviceModelApi.getDeviceModelByType(
  //       device_type,
  //       apiCallback({
  //         translator: trans,
  //         failCallback: errorCallback,
  //         errorCallback,
  //         successCallback,
  //       })
  //     );
  //   } catch (error) {
  //     errorCallback(error);
  //   }
  // };

  // const setDateHandler = (startDatetime, endDateTime) => {
  //   setStartDatetime(startDatetime);
  //   setEndDatetime(endDateTime);
  // };

  const getTranslatedLabel = (labelString) => {
    if (!labelString) return '';

    const upperCaseLabel = labelString.toUpperCase();
    const labelTransformations = [
      { pattern: /\/MIN/g, replacement: '' },
      { pattern: /MG\/DL/g, replacement: 'MG_DL' },
      { pattern: /SPO2 %/g, replacement: 'SPO2' },
      { pattern: / °[CF]?/g, replacement: '' },
      { pattern: /[()]/g, replacement: '' },
      { pattern: /\s+/g, replacement: '_' },
      { pattern: /[/\-]/g, replacement: '_' },
    ];

    let transformedLabel = upperCaseLabel;

    labelTransformations.forEach(({ pattern, replacement }) => {
      transformedLabel = transformedLabel.replace(pattern, replacement);
    });

    transformedLabel = transformedLabel.replace(/_+/g, '_').replace(/^_|_$/g, '');

    return trans(transformedLabel);
  };

  return (
    <>
      <WellnessHistoryModal
        showHistoryModal={showModal}
        modalLoading={modalLoading}
        modalData={modalData}
        modalTitle={modalTitle}
        closeHistoryModal={handleModalClose}
        userMeasurement={loginUserMeasurementUnit?.measurement}
        deviceObj={currentDevice}
        timezone={userTimezone}
        selectedRange={selectedRange}
        setSelectedRange={setSelectedRange}
        is24hoursformat={loginUserTimeformat?.is24hoursformat}
        userDefinedMeasurement={loginUserMeasurementUnit?.measurement}
        insightStartDateTime={insightStartDateTime}
        insightEndDateTime={insightEndDateTime}
        insightData={insightData}
        insightLoading={insightLoading}
        getTranslatedLabel={getTranslatedLabel}
      />
      {/* <WellnessSupportDeviceModal
        showSupportDeviceModal={showSupportDeviceModal}
        closeSupportDeviceModal={() => setShowSupportDeviceModal(false)}
        supportDeviceModalLoading={supportDeviceModalLoading}
        supportDeviceData={supportDeviceData}
        supportDeviceModalTitle={supportDeviceModalTitle}
        activeSupportDevicesTab={activeSupportDevicesTab}
        setActiveSupportDevicesTab={setActiveSupportDevicesTab}
        handleSupportDeviceModal={handleSupportDeviceModal}
        wellnessListData={wellnessListData}
        allDeviceModels={allDeviceModels}
      /> */}
      <div style={{ padding: '40px 40px 0px 40px' }}>
        <WellnessHeader />
        <WellnessList
          showViewHistoryModal={handelShowModal}
          // showSupportDeviceModal={handleSupportDeviceModal}
          mappedData={wellnessListData}
          loading={loading}
          // timeZone={timezone}
          // setDateHandler={setDateHandler}
          // activeSupportDevicesTab={activeSupportDevicesTab}
          // allDeviceModels={allDeviceModels}
          getTranslatedLabel={getTranslatedLabel}
          selectedCard={selectedCard}
          isModalOpen={showModal}
          handleSupportedDevicesClick={handleSupportedDevicesClick}
        />
      </div>
    </>
  );
};

Wellness.propTypes = {
  hubRecord: PropTypes.object,
  timezone: PropTypes.string,
  deviceStatus: PropTypes.object,
  providerid: PropTypes.string,
  openSubMenu: PropTypes.bool,
  loginUserTimeformat: PropTypes.shape({
    is24hoursformat: PropTypes.bool,
  }),
  loginUserMeasurementUnit: PropTypes.shape({
    measurement: PropTypes.string,
  }),
};

export default Wellness;
